/**
 * Helpers for logged-in homeowner (customer) flows. Uses localStorage only.
 */

export function readPublicUser() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function isLoggedInCustomer() {
  const u = readPublicUser();
  return !!(u && u.role === 'customer' && (u.id || u.user_id));
}

export function getCustomerUserId() {
  const u = readPublicUser();
  if (!u || u.role !== 'customer') return null;
  return u.id || u.user_id || null;
}

const Q_PREFIX = 'plantaday_my_questions_';
const YARD_PREFIX = 'plantaday_yard_profile_';

export const YARD_GOAL_CHIPS = [
  { id: 'privacy', label: 'More privacy' },
  { id: 'low-maintenance', label: 'Less maintenance' },
  { id: 'shade', label: 'Shade areas' },
  { id: 'small-yard', label: 'Small yard' },
  { id: 'curb-appeal', label: 'Curb appeal' },
  { id: 'four-season', label: 'Four-season interest' },
];

export function loadSavedQuestions(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(`${Q_PREFIX}${userId}`);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function saveQuestionToAccount(userId, entry) {
  if (!userId || !entry?.id) return;
  const list = loadSavedQuestions(userId);
  const next = list.filter((q) => q.id !== entry.id);
  const notes = Array.isArray(entry.expert_notes) ? entry.expert_notes : [];
  next.unshift({
    id: entry.id,
    question_text: entry.question_text || '',
    ai_answer: entry.ai_answer || '',
    created_at: entry.created_at || new Date().toISOString(),
    expert_notes: notes.map((n) => ({
      contributor_label: n.contributor_label || '',
    })),
  });
  const trimmed = next.slice(0, 100);
  localStorage.setItem(`${Q_PREFIX}${userId}`, JSON.stringify(trimmed));
}

/**
 * Status for saved-question cards from expert note contributor labels.
 */
export function getSavedQuestionDisplayStatus(expertNotes) {
  const notes = Array.isArray(expertNotes) ? expertNotes : [];
  if (notes.length === 0) {
    return { pill: 'AI answered', className: 'bg-orange-100 text-orange-800' };
  }
  const hasJohn = notes.some((n) => /\bjohn\b/i.test(String(n.contributor_label || '')));
  if (hasJohn) {
    return { pill: 'John added a note', className: 'bg-green-100 text-green-800' };
  }
  return { pill: 'Designer note added', className: 'bg-blue-100 text-blue-800' };
}

export function goalLabelById(goalId) {
  const g = YARD_GOAL_CHIPS.find((c) => c.id === goalId);
  return g ? g.label : '';
}

/** Prefix prepended to Ask AI prompts when yard profile exists (not shown in UI). */
export function buildYardContextPromptPrefix(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const city = String(profile.cityRegion || '').trim();
  const goal = goalLabelById(profile.goalId);
  if (!city && !goal) return '';
  const parts = [];
  if (city) parts.push(`City or region: ${city}.`);
  if (goal) parts.push(`Main yard goal: ${goal}.`);
  return `Homeowner context for this question (${parts.join(' ')}). Use it to localize the answer. Do not repeat this sentence back as a heading.\n\n`;
}

export function loadYardProfile(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(`${YARD_PREFIX}${userId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveYardProfile(userId, profile) {
  if (!userId) return;
  localStorage.setItem(`${YARD_PREFIX}${userId}`, JSON.stringify(profile));
}
